// ════════════════════════════════════════════════════════════════════════════
//  ManDana — workflows
//  ---------------------------------------------------------------------------
//  Where something stands, in two layers: SIX CATEGORIES the machine owns, and
//  as many STATUSES as the user wants standing on top of them.
//
//  WHY TWO LAYERS. The app knew four words — backlog · active · parked · done —
//  and they were the right four for shipping software and the wrong four for
//  memorising a Surah. The easy fix is to let anybody name anything, and it is
//  also the fix that makes the app go quiet: once Career says "In Development"
//  and Spirit says "Active Practice", nothing can put them on one screen,
//  because nothing knows they mean the same thing. So the user's words stand on
//  categories the app owns and never asks about, and a single view can still
//  answer "what am I in the middle of" without ever having read the vocabulary.
//
//  SIX, NOT SEVEN. The sketch this came from had a seventh category for
//  DUPLICATE. A duplicate is a RELATION between two records — a pointer at the
//  one that survived — not a place either of them stands, and keeping it would
//  have spent a permanent line in every menu the user ever opens, in nine
//  domains where nobody has ever marked anything a duplicate.
//
//  THE SPINE IS CODE, NEVER DATA. It is frozen and exported frozen: a category
//  is a promise to every status already written against it, and a "tidy-up"
//  that renamed one would silently restyle somebody's whole history. Same
//  argument md-domain.ts makes about the ten ids.
//
//  THE DEFAULT WORKFLOW IS CODE, AND A PERSON'S EDIT OF IT IS A DOCUMENT. It is
//  materialised on read, the way md-project.ts does the built-in Prayers
//  project — and, like Prayers, what the user changes about it is stored beside
//  it rather than forbidden. A stored row wearing the built-in id stands IN
//  FRONT OF the code copy; it never becomes a second workflow in the list, and
//  deleting it puts the app's own words back.
//
//  That is what makes the built-in safe to let go of: the words cannot be LOST,
//  only stood in front of. The alternative — a read-only default with Duplicate
//  as the only door — would make the commonest wish in the whole feature ("we
//  say Doing here, not In Progress") cost ten domain assignments and STILL
//  leave the old words as the fallback for anything created later.
//
//  ITS STATUS IDS ARE THE WORDS ALREADY IN STORAGE. `backlog`, `active`,
//  `parked` and `done` have been written into project documents since the field
//  existed, so they are kept as ids rather than replaced. Nothing is rewritten,
//  no migration runs, and a device on an older build still reads its own files.
//  `active` SHOWS as "In Progress" and `parked` as "Paused" — an id is not a
//  name, which is the whole reason ids are stable and names are free.
//
//  THE CASCADE, AND WHY ASSIGNMENTS ARE THEIR OWN DOCUMENT. A workflow resolves
//  down one chain — the node's own assignment, then its pursuit's, then its
//  domain's, then the default — so setting one on Spirit gives every pursuit,
//  project and record under it the same vocabulary, and setting one lower is a
//  deliberate breakout that a parent's later change flows around.
//
//  The plan put that pointer on a project row, a pursuit row and a domain
//  DOCUMENT. The third would not have survived contact: a domain's document IS
//  its writing, mdDomainTextWrite REMOVES it when the writing is emptied, and
//  mdDomainSyncDocs skips it while the text is blank — so an assignment kept
//  there would be deleted by clearing a paragraph and would never sync from a
//  domain nobody had written in. All three levels are kept in one small
//  assignments document instead, which is also what this app's own rule asks
//  for: an assignment is SCHEMA and must not share a document with content that
//  changes for unrelated reasons.
//
//  A POINTER AT A DELETED WORKFLOW FALLS THROUGH TO THE PARENT, not past it to
//  the default — which would silently overrule a domain-level choice the person
//  still holds. Nothing is rewritten when a workflow is deleted, so there is no
//  cleanup to get wrong and no gesture that quietly edits ten other documents.
//
//  AN UNKNOWN CATEGORY IS NOT A BROKEN ONE. A status naming a category this
//  build has never heard of round-trips untouched, so a newer client's workflow
//  survives being opened on an older one. Only malformed rows are refused.
//  Repair, not reject — md-sync.js's bias, one layer down.
// ════════════════════════════════════════════════════════════════════════════

/** One of the six meanings the machine reads. `id` is stored on every status;
    `label` is only ever a heading on the Statuses page. */
interface MdSpineCategory {
  id: string;
  label: string;
}

/** One status, in the user's own word. `category` says what the machine reads
    it as; `position` orders it inside that category and is a float so a status
    can be inserted between two others without renumbering the rest. */
interface MdWorkflowStatus {
  id: string;
  name: string;
  category: string;
  position: number;
  color?: string;
}

/** Why a door said no. The six `last_*` are this module's own: every other
    reason is one md-project.ts already answers with, and the Statuses page
    turns each into a sentence rather than a code.

    SIX REASONS AND NOT ONE, though they all mean "that category would be
    empty". The page says them out loud, and the reason a workflow needs
    somewhere to set things down is not the reason it needs somewhere to
    finish — a single lumped reason would make all six say the same sentence
    and leave the person to work out which category it was about. */
type MdWorkflowRefusal = 'agent' | 'builtin' | 'missing' | 'invalid'
  | 'last_backlog' | 'last_planned' | 'last_progress'
  | 'last_paused' | 'last_completed' | 'last_cancelled' | 'storage_full';

/** What a door answers. `id` rides along on a create, because the surface that
    just made something needs to draw it before the next read. */
interface MdWorkflowResult {
  ok: boolean;
  id?: string;
  reason?: MdWorkflowRefusal;
}

/** A named set of statuses. Nothing points at a workflow from in here — the
    cascade that decides which one a project reads lives in md-project.ts's
    neighbourhood, because a pointer belongs with the thing that carries it. */
interface MdWorkflow {
  id: string;
  name: string;
  statuses: MdWorkflowStatus[];
}

(function (g: any) {
  'use strict';

  const K_INDEX = 'md:workflows';
  const K_USE = 'md:workflowUse';
  const DOC = 'workflows';
  const DOC_USE = 'workflow-use';
  const NAME_MAX = 60;
  /* Caps, not policy. Nothing in the app offers a hundredth workflow; these
     exist so a corrupt or hostile pull cannot make a render loop unbounded. */
  const MAX_WORKFLOWS = 60;
  const MAX_STATUSES = 60;

  /* In the order they are read, which is the order the Statuses page draws its
     six sections. Paused sits after In Progress because that is where the eye
     expects it — NOT because pausing is progress. Its mark says "set down". */
  const MD_SPINE: MdSpineCategory[] = [
    { id: 'backlog', label: 'Backlog' },
    { id: 'planned', label: 'Planned' },
    { id: 'progress', label: 'In Progress' },
    { id: 'paused', label: 'Paused' },
    { id: 'completed', label: 'Completed' },
    { id: 'cancelled', label: 'Cancelled' },
  ];
  for (const category of MD_SPINE) Object.freeze(category);
  Object.freeze(MD_SPINE);

  /* Where each category reads, so a workflow's statuses come back in the order
     every surface draws them: SPINE ORDER FIRST, then position inside it. Pure
     position across categories would let a Completed status read before a
     Planned one, which is an order no screen in the app wants. A category this
     build has never heard of sorts last rather than first — it is somebody
     else's word, and it should not open the list. */
  const SPINE_AT = new Map(MD_SPINE.map((row, at) => [row.id, at]));
  const rank = (row: MdWorkflowStatus): number =>
    SPINE_AT.has(row.category) ? (SPINE_AT.get(row.category) as number) : MD_SPINE.length;

  const DEFAULT_ID = 'default';

  /* Ships with the app; see the header. Two statuses stand in BACKLOG because
     Inbox is where everything unfiled already is and Backlog is where things go
     once somebody has looked at them — and because a workflow whose first
     Backlog status is also its only one has nothing to demonstrate. */
  const DEFAULT: MdWorkflow = {
    id: DEFAULT_ID,
    name: 'Default',
    statuses: [
      { id: 'inbox', name: 'Inbox', category: 'backlog', position: 1 },
      { id: 'backlog', name: 'Backlog', category: 'backlog', position: 2 },
      { id: 'planned', name: 'Planned', category: 'planned', position: 3 },
      { id: 'active', name: 'In Progress', category: 'progress', position: 4 },
      { id: 'parked', name: 'Paused', category: 'paused', position: 5 },
      { id: 'done', name: 'Completed', category: 'completed', position: 6 },
      { id: 'cancelled', name: 'Cancelled', category: 'cancelled', position: 7 },
    ],
  };
  const BUILTIN_IDS = new Set([DEFAULT_ID]);

  const ID = /^[a-z0-9]{6,40}$/;
  const STATUS_ID = /^[a-z0-9]{1,64}$/;

  function isStatus(row: any): row is MdWorkflowStatus {
    return !!row && typeof row === 'object'
      && typeof row.id === 'string' && STATUS_ID.test(row.id)
      && typeof row.name === 'string' && !!row.name.trim()
      && typeof row.category === 'string' && !!row.category
      && Number.isFinite(row.position);
  }

  /* A status is copied field by field rather than spread, so a stored document
     carrying a key this build has never heard of cannot smuggle it back out
     into a screen that would then draw it. The CATEGORY is the one unknown
     value that is deliberately kept — see the header. */
  function readStatus(row: MdWorkflowStatus): MdWorkflowStatus {
    const out: MdWorkflowStatus = {
      id: row.id, name: row.name.trim().slice(0, NAME_MAX),
      category: row.category, position: row.position,
    };
    if (typeof row.color === 'string' && row.color) out.color = row.color;
    return out;
  }

  function isRow(row: any): row is MdWorkflow {
    return !!row && typeof row === 'object'
      && typeof row.id === 'string' && ID.test(row.id)
      && typeof row.name === 'string' && !!row.name.trim()
      && Array.isArray(row.statuses);
  }

  /** The statuses come back sorted — spine category, then position within it —
      so no caller has to remember to do it and no two surfaces can disagree
      about what order a vocabulary reads in. */
  function readRow(row: MdWorkflow): MdWorkflow {
    return {
      id: row.id,
      name: row.name.trim().slice(0, NAME_MAX),
      statuses: row.statuses.filter(isStatus).slice(0, MAX_STATUSES).map(readStatus)
        .sort((a, b) => rank(a) - rank(b) || a.position - b.position),
    };
  }

  function readIndex(): MdWorkflow[] {
    const raw = g.mdRead(K_INDEX, []);
    if (!Array.isArray(raw)) return [];
    const out: MdWorkflow[] = [];
    const seen = new Set<string>();
    for (const row of raw) {
      if (!isRow(row) || seen.has(row.id)) continue;   // first writer wins a repeated id
      seen.add(row.id);
      out.push(readRow(row));
    }
    return out;
  }

  /** After the last one in a category, which is where a new status lands and
      where one arriving from another category lands. Global floats, so "last
      overall" is also "last within its category". */
  const endPosition = (flow: MdWorkflow): number =>
    flow.statuses.reduce((high, row) => Math.max(high, row.position), 0) + 1;

  /* ═══ made whole ══════════════════════════════════════════════════════════
     EVERY WORKFLOW HAS A STATUS IN EVERY CATEGORY (Marwan, 2026-08-18). The
     floor used to be two — somewhere to start and somewhere to finish — and a
     workflow standing on it could have no In Progress at all. That was
     defensible while a board drew four words of its own; it stopped being
     defensible the moment a project's record board started drawing THIS
     vocabulary, because a workflow with a hole in the middle became a board
     with a hole in the middle, and no gesture available to the person looking
     at it would fill the hole. The column they need does not exist to be
     dropped into.

     A FLOOR, NOT A CEILING. A category may hold as many statuses as somebody
     wants; this only insists that none of the six is empty.

     REPAIRED ON READ, AND NOTHING IS WRITTEN. Short workflows are already on
     real devices. Opening the document to add what is missing would be a
     change nobody asked for, arriving everywhere at once, to a document that
     another device may be reading with an older build — so the fill happens on
     the way OUT, the same move md-project.ts's `materialise` makes and the
     same one "absence is Inbox" makes.

     WHICH IS WHY THIS IS NOT A LINE INSIDE readRow. mdWorkflowSyncDocs reads
     readIndex(), so filling one floor lower would push every filler onto the
     wire the first time any device synced. Three readers, three answers:
     readIndex is what is stored, mdWorkflowIndex is what is stored made whole,
     and storedWith is the edited flow made whole — the one place a filler ever
     gets written down.

     THE COST OF THAT LAST ONE, SAID OUT LOUD. A filler is a real status: it
     can be renamed and recoloured like any other, and edits go through the
     stored list, so the first edit to a short workflow writes its fillers down
     along with the edit. Once, for that workflow only. The same bargain the
     built-in default already makes one screen up.

     MAX_STATUSES stays a cap on what is STORED. A workflow can read back at
     most five rows over it, which is a render bound rather than a policy. */

  /** The default workflow's own id for each category. `mdWorkflowTryCreate`
      copies those ids on purpose — a project moved from one workflow to
      another still resolves, because both vocabularies use `active` for
      whatever each of them calls In Progress — and a filler is the same
      argument: a record already wearing the word resolves in a workflow that
      never stored it. */
  const FILLER_ID: Record<string, string> = {
    backlog: 'backlog', planned: 'planned', progress: 'active',
    paused: 'parked', completed: 'done', cancelled: 'cancelled',
  };

  /** An id nothing in this workflow is already wearing. Nothing stopped
      somebody dragging `active` out of In Progress and into Paused before this
      phase, so the natural id can genuinely be taken by a status standing
      somewhere else. The walk is DETERMINISTIC because two devices fill the
      same short workflow independently and neither of them writes anything —
      an id chosen at random would give them two different answers for the same
      status, and a record moved onto it on one device would read as nothing on
      the other. */
  function freeId(taken: Set<string>, category: string): string {
    for (const id of [FILLER_ID[category] || category, category, `md${category}`]) {
      if (!taken.has(id)) return id;
    }
    let n = 2;
    while (taken.has(`md${category}${n}`)) n += 1;
    return `md${category}${n}`;
  }

  /** The same workflow with every empty spine category occupied, or the very
      same object when none of them is. A filler lands AFTER the last position,
      so it can never displace the status a category already had — the first
      BACKLOG status is still whatever was stored (WF-2's Inbox) and the last
      COMPLETED one is still what the tick means (WR-3). A category this build
      has never heard of is somebody else's word and is left exactly where it
      is: repair, not reject. */
  function whole(flow: MdWorkflow): MdWorkflow {
    const held = new Set(flow.statuses.map((row) => row.category));
    const short = MD_SPINE.filter((category) => !held.has(category.id));
    if (!short.length) return flow;
    const taken = new Set(flow.statuses.map((row) => row.id));
    const statuses = flow.statuses.slice();
    let at = endPosition(flow);
    for (const category of short) {
      const id = freeId(taken, category.id);
      taken.add(id);
      statuses.push({ id, name: category.label, category: category.id, position: at });
      at += 1;
    }
    return { id: flow.id, name: flow.name,
      statuses: statuses.sort((a, b) => rank(a) - rank(b) || a.position - b.position) };
  }

  /** Every workflow, the default first — it is the one nothing can delete and
      the one everything falls back to, so it is the one that reads first. A
      stored edit of it takes its place rather than joining the list. */
  function mdWorkflowIndex(): MdWorkflow[] {
    const rows = readIndex();
    const mine = rows.find((row) => row.id === DEFAULT_ID);
    return [whole(mine || readRow(DEFAULT)),
      ...rows.filter((row) => row.id !== DEFAULT_ID).map(whole)];
  }

  function mdWorkflowById(id: unknown): MdWorkflow | null {
    if (typeof id !== 'string') return null;
    return mdWorkflowIndex().find((row) => row.id === id) || null;
  }

  /** One status, asked of the workflow that owns it. Deliberately takes the
      workflow: a status id is only meaningful inside one, and a lookup that
      searched every workflow would happily answer with somebody else's word. */
  function mdWorkflowStatusById(workflowId: unknown, statusId: unknown): MdWorkflowStatus | null {
    const flow = mdWorkflowById(workflowId);
    if (!flow || typeof statusId !== 'string') return null;
    return flow.statuses.find((row) => row.id === statusId) || null;
  }

  /* ═══ everything is somewhere ═════════════════════════════════════════════
     The app shipped a fifth project status called "Not saying", which was an
     ABSENCE wearing the costume of a choice — a row in every menu, a dotted
     ring on every board, and no answer to "what is sitting undecided across my
     whole life", because nothing was there to count. It is replaced by a real
     status in the BACKLOG category, and absence in storage RESOLVES to it.

     Nothing is rewritten to make that true. Every project document written
     since the field existed keeps its stored word, no backfill runs, and a
     device on an older build still reads its own files — the same move
     materialise() already makes for a project's domain, one layer up. */

  /** The status absence resolves to: the FIRST BACKLOG STATUS BY POSITION.

      Positional, not keyed to the id `inbox`, so a person who renames it to
      "Ideas" or deletes it in favour of their own first Backlog word never
      opens a hole. Keying the rule to the English word would have made that
      word load-bearing in an app built for a life lived in two languages. */
  function mdWorkflowInboxOf(workflowId: unknown): MdWorkflowStatus | null {
    const flow = mdWorkflowById(workflowId);
    if (!flow || !flow.statuses.length) return null;
    /* Statuses arrive sorted, so first-found is first-by-position. The floor
       (WF-3) stops a person reaching a workflow with no Backlog through the
       doors; a document pulled from a broken or older client can still arrive
       in that state, and "everything is somewhere" has to hold anyway — so the
       first status of ANY category answers rather than nothing. Inventing a
       Backlog status the workflow does not have would be worse: menus would
       start offering a word nobody wrote. */
    return flow.statuses.find((row) => row.category === 'backlog') || flow.statuses[0] || null;
  }

  /** The status a TICK means: the LAST status in the COMPLETED category.

      Marwan's ruling of 2026-08-18 — not the first, not the category at large,
      the terminal word. In the default workflow that is `Completed`, which is
      what a tick has always meant, so nothing anybody has already ticked
      changes meaning under them. A workflow whose finish is named `Shipped`
      makes the tick mean Shipped, everywhere, untold.

      Null, and deliberately WITHOUT Inbox's fall-through to the first status of
      any category. Inbox answers where a thing STANDS and something always has
      to; this answers what a gesture means, and a workflow with nowhere to
      finish gives that gesture nothing to say. The floor (WF-3) stops anybody
      reaching that state through the doors; a document pulled from a broken
      client can still arrive in it, and the honest answer there is that the
      tick moves nothing rather than that it moves something arbitrary. */
  function mdWorkflowFinishOf(workflowId: unknown): MdWorkflowStatus | null {
    const flow = mdWorkflowById(workflowId);
    if (!flow) return null;
    const done = flow.statuses.filter((row) => row.category === 'completed');
    return done.length ? (done[done.length - 1] as MdWorkflowStatus) : null;
  }

  /** Every status id anybody's workflow declares, in the order they are read.

      ONE LADDER FOR A SORT, not a vocabulary. md-views.ts orders a field by
      position in a declared list, and a record board sorted by status has to
      put Backlog before Done rather than alphabetically. Every board that draws
      this field is one project's, so its statuses all come from one workflow
      and sit contiguous and in order inside this list; the order BETWEEN
      workflows is arbitrary and never rendered, because two workflows' statuses
      are never sorted against each other. */
  function mdWorkflowStatusLadder(): string[] {
    const seen: Record<string, boolean> = {};
    const out: string[] = [];
    for (const flow of mdWorkflowIndex()) {
      for (const status of flow.statuses) {
        if (seen[status.id]) continue;
        seen[status.id] = true; out.push(status.id);
      }
    }
    return out;
  }

  /** What a stored word MEANS, in the workflow that is being read through.

      A word from another vocabulary resolves to Inbox and is NOT rewritten:
      reassigning a project's pursuit can change its workflow under it, and
      putting the pursuit back must put the status back. Resolution is a
      rendering decision — only a person moves something. */
  function mdWorkflowResolveStatus(workflowId: unknown, statusId: unknown): MdWorkflowStatus | null {
    const flow = mdWorkflowById(workflowId);
    if (!flow) return null;
    const found = typeof statusId === 'string' && statusId
      ? flow.statuses.find((row) => row.id === statusId) : null;
    return found || mdWorkflowInboxOf(flow.id);
  }

  /** The first status standing in a category, inside one workflow — what a
      SPINE column means for a card that reads through this workflow (WF-13).

      IT NO LONGER ANSWERS NULL for a category the machine knows. When this was
      written the floor was Backlog and Completed and the other four were
      optional, so a card could legitimately be looking at a column its own
      vocabulary had nothing standing in. Since WK-1 every workflow is read
      whole and there is always something here. The null is kept for the one
      question that can still have no answer — a category name this build has
      never heard of — and for callers that ask before knowing which. */
  function mdWorkflowCategoryLanding(workflowId: unknown, category: unknown): MdWorkflowStatus | null {
    const flow = mdWorkflowById(workflowId);
    if (!flow || typeof category !== 'string') return null;
    // `statuses` already reads spine-first-then-position, so the first match IS
    // the first one — no second sort, and no second opinion about the order.
    return flow.statuses.find((row) => row.category === category) || null;
  }

  /** The spine category a status stands on, or null for a status naming one
      this build has never heard of. Callers that draw a mark ask this, so an
      unknown category draws as nothing rather than as somebody else's shape. */
  function mdWorkflowCategoryOf(status: unknown): MdSpineCategory | null {
    const id = status && typeof status === 'object'
      ? (status as MdWorkflowStatus).category : status;
    return (typeof id === 'string' && MD_SPINE.find((row) => row.id === id)) || null;
  }

  /* ═══ the doors ═══════════════════════════════════════════════════════════
     Every one answers WHY rather than false, because the Statuses page has to
     say something true when a press does not land — "nothing happened" is the
     one answer a person cannot act on. Same contract md-project.ts's doors
     keep, for the same reason.

     THE FLOOR IS BACKLOG AND COMPLETED, and it is enforced here and nowhere
     else. A document arriving from a broken client is repaired as it renders
     (see mdWorkflowInboxOf), never rejected: no read may depend on a rule a
     person could only have broken by going around these functions. */

  const OK: MdWorkflowResult = { ok: true };
  const refuse = (reason: MdWorkflowRefusal): MdWorkflowResult => ({ ok: false, reason });

  function writeRefusal(): MdWorkflowResult {
    const fault = typeof g.mdStorageHealth === 'function' ? g.mdStorageHealth().lastFault : null;
    return refuse(fault && fault.quota ? 'storage_full' : 'invalid');
  }

  /* Nothing derives a status, and nothing NAMES one. md-project.ts already
     refuses to let a machine move something between statuses; letting one
     invent the statuses would be the larger version of the same mistake. */
  function agentRefusal(): MdWorkflowResult | null {
    return typeof g.mdOrganizerActor === 'function' && g.mdOrganizerActor() === 'agent'
      ? refuse('agent') : null;
  }

  function newId(): string {
    let s = '';
    while (s.length < 10) s += Math.random().toString(36).slice(2).replace(/[^a-z0-9]/g, '');
    return s.slice(0, 10);
  }

  const clean = (name: unknown): string =>
    (typeof name === 'string' ? name : '').trim().slice(0, NAME_MAX);

  /* Editing the default is editing a built-in, so the first such edit
     materialises the code copy into the stored list — the same moment
     md-project.ts writes a built-in's home document. Everything after that is
     an ordinary row. */
  function storedWith(flowId: string): MdWorkflow[] {
    const rows = readIndex();
    const at = rows.findIndex((row) => row.id === flowId);
    /* Made whole HERE and nowhere else in the write path, so an edit to a short
       workflow writes its missing statuses down alongside the edit — and leaves
       every other workflow in the document exactly as it found it. */
    if (at >= 0) {
      const out = rows.slice();
      out[at] = whole(rows[at] as MdWorkflow);
      return out;
    }
    if (!BUILTIN_IDS.has(flowId)) return rows;
    return [whole(readRow(DEFAULT)), ...rows];
  }

  /** Whether taking `status` out of its category would empty a category that
      may not be emptied — which since WK-2 is ANY of the six. Called on delete
      and on a move that CHANGES category; a reorder inside one is not an exit
      and must not be caught by it.

      THE FLOOR MOVED FROM TWO TO SIX and it is worth writing down why, because
      the narrower rule was argued for and was right at the time. Phase 5 held
      that a workflow needs somewhere to start and somewhere to finish, and
      that insisting on Planned in a vocabulary whose author has no use for it
      is ceremony. Phase 6 then made a project's record board draw exactly this
      vocabulary — and a workflow with no In Progress became a board with no In
      Progress column, with no gesture available to the person looking at it
      that would make one. An unused row costs a line on a settings page; a
      missing place costs somebody the ability to say they have started.

      A category the machine does not know is not floored: it is somebody
      else's word, arriving from a newer client, and this build has no standing
      to insist it keeps it. */
  function floorBreak(flow: MdWorkflow, status: MdWorkflowStatus): MdWorkflowRefusal | null {
    if (flow.statuses.filter((row) => row.category === status.category).length > 1) return null;
    if (!SPINE_AT.has(status.category)) return null;
    return `last_${status.category}` as MdWorkflowRefusal;
  }

  /** Find the workflow, let `change` edit it in place or name a refusal, and
      write the whole list once. One storage task per gesture, so ⌘Z takes back
      the whole of it rather than half. */
  function mutate(flowId: unknown,
    change: (flow: MdWorkflow) => MdWorkflowRefusal | null): MdWorkflowResult {
    const agent = agentRefusal();
    if (agent) return agent;
    if (typeof flowId !== 'string' || !mdWorkflowById(flowId)) return refuse('missing');
    const rows = storedWith(flowId);
    const flow = rows.find((row) => row.id === flowId) as MdWorkflow;
    const why = change(flow);
    if (why) return refuse(why);
    return g.mdWrite(K_INDEX, rows) ? OK : writeRefusal();
  }

  /** A new workflow opens as a copy of the account's default — not as six empty
      sections, which is a form, and this app does not open with a form.

      IT KEEPS THE DEFAULT'S STATUS IDS, which buys something real: a project
      moved from one workflow to the other still resolves, because both
      vocabularies use `active` for whatever each of them calls In Progress. */
  function mdWorkflowTryCreate(name: unknown): MdWorkflowResult {
    const agent = agentRefusal();
    if (agent) return agent;
    const label = clean(name);
    if (!label) return refuse('invalid');
    const rows = readIndex();
    if (rows.length >= MAX_WORKFLOWS) return refuse('invalid');
    const base = mdWorkflowById(DEFAULT_ID) as MdWorkflow;
    const row: MdWorkflow = { id: newId(), name: label,
      statuses: base.statuses.map((status) => ({ ...status })) };
    return g.mdWrite(K_INDEX, [...rows, row]) ? { ok: true, id: row.id } : writeRefusal();
  }

  function mdWorkflowTryRename(id: unknown, name: unknown): MdWorkflowResult {
    return mutate(id, (flow) => {
      const label = clean(name);
      if (!label) return 'invalid';
      flow.name = label;
      return null;
    });
  }

  /** Gone. The default is not deletable — it is the floor under the whole
      cascade, and an account with no vocabulary at all is not a state the app
      has anything to draw. mdWorkflowTryReset is the door that gesture wants. */
  function mdWorkflowTryDelete(id: unknown): MdWorkflowResult {
    const agent = agentRefusal();
    if (agent) return agent;
    if (typeof id === 'string' && BUILTIN_IDS.has(id)) return refuse('builtin');
    const rows = readIndex();
    if (!rows.some((row) => row.id === id)) return refuse('missing');
    return g.mdWrite(K_INDEX, rows.filter((row) => row.id !== id)) ? OK : writeRefusal();
  }

  /** Put the app's own words back. Only a built-in has words to go back to, so
      this refuses everything else rather than pretending to do something.

      It removes ONE ROW, not the document: the workflows share one, and a
      naive reset would take somebody's Spiritual Track with it. */
  function mdWorkflowTryReset(id: unknown): MdWorkflowResult {
    const agent = agentRefusal();
    if (agent) return agent;
    if (typeof id !== 'string' || !BUILTIN_IDS.has(id)) return refuse('invalid');
    const rows = readIndex().filter((row) => row.id !== id);
    if (rows.length) return g.mdWrite(K_INDEX, rows) ? OK : writeRefusal();
    // Nothing left to keep — and nothing to write if there never was anything.
    if (g.mdRead(K_INDEX, null) == null) return OK;
    return g.mdRemove(K_INDEX) ? OK : writeRefusal();
  }

  function mdWorkflowTryStatusCreate(flowId: unknown, category: unknown,
    name: unknown): MdWorkflowResult {
    let made = '';
    const out = mutate(flowId, (flow) => {
      const label = clean(name);
      if (!label || !mdWorkflowCategoryOf(category)) return 'invalid';
      if (flow.statuses.length >= MAX_STATUSES) return 'invalid';
      const taken = new Set(flow.statuses.map((row) => row.id));
      do { made = newId(); } while (taken.has(made));
      flow.statuses.push({ id: made, name: label, category: category as string,
        position: endPosition(flow) });
      return null;
    });
    return out.ok ? { ok: true, id: made } : out;
  }

  /** A rename never moves the id under it, which is the whole reason ids are
      stable: every project and record already wearing this status keeps
      wearing it, and the change is one word in one document. */
  function mdWorkflowTryStatusRename(flowId: unknown, statusId: unknown,
    name: unknown): MdWorkflowResult {
    return mutate(flowId, (flow) => {
      const status = flow.statuses.find((row) => row.id === statusId);
      if (!status) return 'missing';
      const label = clean(name);
      if (!label) return 'invalid';
      status.name = label;
      return null;
    });
  }

  /* Decoration inside a category, never meaning: the CATEGORY owns the shape,
     and the shape is what a person reads with the hue turned off. Only a
     literal colour is accepted — a value from a document is about to be handed
     to a style attribute, and `javascript:` is a string too. */
  const COLOR = /^#[0-9a-fA-F]{3,8}$/;

  function mdWorkflowTryStatusSetColor(flowId: unknown, statusId: unknown,
    color: unknown): MdWorkflowResult {
    return mutate(flowId, (flow) => {
      const status = flow.statuses.find((row) => row.id === statusId);
      if (!status) return 'missing';
      const wipe = color == null || color === '';
      if (!wipe && !(typeof color === 'string' && COLOR.test(color))) return 'invalid';
      if (wipe) delete status.color; else status.color = color as string;
      return null;
    });
  }

  /** Reorder inside a category, or move to another one — one door, because to
      the hand doing the dragging it is one gesture, and the mark changing as it
      lands is the whole explanation of what just happened.

      `position` is a float so a status can be dropped between two others
      without renumbering the rest; omit it to land at the end. */
  function mdWorkflowTryStatusMove(flowId: unknown, statusId: unknown, category: unknown,
    position?: unknown): MdWorkflowResult {
    return mutate(flowId, (flow) => {
      const status = flow.statuses.find((row) => row.id === statusId);
      if (!status) return 'missing';
      if (!mdWorkflowCategoryOf(category)) return 'invalid';
      const leaving = status.category !== category;
      if (leaving) {
        const why = floorBreak(flow, status);
        if (why) return why;
      }
      if (position != null && !Number.isFinite(position)) return 'invalid';
      status.category = category as string;
      status.position = position == null
        ? (leaving ? endPosition(flow) : status.position) : position as number;
      return null;
    });
  }

  /** The wall a removal would hit, or null — asked BEFORE anything is offered,
      so the page never opens a "move them where?" sheet onto a question whose
      answer is no. The floor rule stated once, here, and read from there. */
  function mdWorkflowStatusRemovable(flowId: unknown, statusId: unknown): MdWorkflowRefusal | null {
    const flow = mdWorkflowById(flowId);
    if (!flow) return 'missing';
    const status = flow.statuses.find((row) => row.id === statusId);
    if (!status) return 'missing';
    return floorBreak(flow, status);
  }

  /** Take a status away, and say where everything standing on it goes.

      A STATUS IS NOT DELETED ALONE. Fourteen records pointing at a word that is
      no longer in the vocabulary would read as Inbox for ever without anybody
      choosing that, and a vocabulary swapped back would find them still saying
      it. So the landing place is an ARGUMENT, the holders are rewritten in the
      SAME synchronous task as the removal, and ⌘Z therefore takes back the
      status and all fourteen together — one gesture, one step (md-history.ts).

      `moveTo` omitted means Inbox, which is the honest default: it is where
      everything that has said nothing already stands, and it is the one place
      guaranteed to exist by the floor. When Inbox is the status LEAVING, the
      landing place falls to whatever will become the first status instead.

      A FAILED WRITE MID-CASCADE LEAVES STORAGE AS IT WAS. The holders are moved
      first and their documents are kept; if the removal itself then fails, the
      move is unwound rather than left standing on its own. */
  function mdWorkflowTryStatusDelete(flowId: unknown, statusId: unknown,
    moveTo?: unknown): MdWorkflowResult {
    const agent = agentRefusal();
    if (agent) return agent;
    const flow = mdWorkflowById(flowId);
    if (!flow) return refuse('missing');
    const status = flow.statuses.find((row) => row.id === statusId);
    if (!status) return refuse('missing');
    const why = floorBreak(flow, status);
    if (why) return refuse(why);

    const inbox = mdWorkflowInboxOf(flow.id);
    const fallback = inbox && inbox.id !== statusId ? inbox
      : flow.statuses.find((row) => row.id !== statusId) || null;
    const landing = moveTo == null || moveTo === '' ? (fallback ? fallback.id : null) : moveTo;
    if (typeof landing !== 'string' || landing === statusId
      || !flow.statuses.some((row) => row.id === landing)) return refuse('invalid');

    const moved = g.mdProjectStatusMoveAll
      ? g.mdProjectStatusMoveAll(flow.id, statusId, landing) : { ok: true, undo: () => {} };
    if (!moved.ok) return refuse(moved.reason || 'invalid');

    const out = mutate(flowId, (row) => {
      row.statuses = row.statuses.filter((item) => item.id !== statusId);
      return null;
    });
    if (!out.ok) moved.undo();
    return out;
  }

  /* ═══ the cascade ═════════════════════════════════════════════════════════
     One document holds every assignment, at all three levels, keyed by a one
     letter prefix so a domain id and a project id can never collide. See the
     header for why a domain's own document could not hold its own. */

  const USE_PREFIX: Record<string, string> = { domain: 'd:', pursuit: 'u:', project: 'p:' };

  function readUse(): Record<string, string> {
    const raw = g.mdRead(K_USE, null);
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
      if (typeof value !== 'string' || !ID.test(value)) continue;
      const kind = Object.entries(USE_PREFIX).find(([, mark]) => key.indexOf(mark) === 0);
      if (!kind || !nodeExists(kind[0], key.slice(kind[1].length))) continue;
      out[key] = value;
    }
    return out;
  }

  /** Whether the thing an assignment names is still there. Asked on READ, so a
      project deleted on another device takes its assignment out of the document
      the next time one is written, without a gesture that edits ten documents. */
  function nodeExists(kind: string, id: string): boolean {
    if (kind === 'domain') return !!(g.mdDomainById && g.mdDomainById(id));
    if (kind === 'pursuit') return !!(g.mdPursuitById && g.mdPursuitById(id));
    if (kind === 'project') return !!(g.mdProjectById && g.mdProjectById(id));
    return false;
  }

  /** The workflow assigned AT this node — not the one it resolves to. The
      "Used by" tree needs both, and telling them apart is the difference
      between a filled box and a half-filled one. */
  function mdWorkflowUse(kind: unknown, id: unknown): string | null {
    if (typeof kind !== 'string' || typeof id !== 'string' || !USE_PREFIX[kind]) return null;
    return readUse()[USE_PREFIX[kind] + id] || null;
  }

  /** An assignment that still points at a workflow that exists. A dead pointer
      is left in the document deliberately (see the header) and simply does not
      answer here, so resolution walks on to the parent. */
  function liveUse(kind: string, id: string): string | null {
    const assigned = mdWorkflowUse(kind, id);
    return assigned && mdWorkflowById(assigned) ? assigned : null;
  }

  interface MdWorkflowSource {
    workflowId: string;
    from: 'project' | 'pursuit' | 'domain' | 'default';
    fromId?: string;
  }

  /** Which workflow a node resolves to, AND where that came from — the second
      half is what lets the chip say "Spiritual Track · from Spirit" instead of
      naming a workflow and leaving the person to guess who decided. */
  function mdWorkflowSourceFor(kind: unknown, id: unknown): MdWorkflowSource {
    const fallback: MdWorkflowSource = { workflowId: DEFAULT_ID, from: 'default' };
    if (typeof kind !== 'string' || typeof id !== 'string' || !USE_PREFIX[kind]) return fallback;

    let domainId = kind === 'domain' ? id : '';
    let pursuitId = kind === 'pursuit' ? id : '';

    if (kind === 'project') {
      const project = g.mdProjectById ? g.mdProjectById(id) : null;
      if (!project) return fallback;
      const own = liveUse('project', id);
      if (own) return { workflowId: own, from: 'project', fromId: id };
      pursuitId = project.pursuitId || '';
      domainId = project.domainId || '';        // already rewritten from the pursuit on read
    }

    if (pursuitId) {
      const pursuit = g.mdPursuitById ? g.mdPursuitById(pursuitId) : null;
      if (pursuit) {
        const own = liveUse('pursuit', pursuitId);
        if (own) return { workflowId: own, from: 'pursuit', fromId: pursuitId };
        domainId = pursuit.domainId || domainId;
      }
    }

    if (domainId && g.mdDomainById && g.mdDomainById(domainId)) {
      const own = liveUse('domain', domainId);
      if (own) return { workflowId: own, from: 'domain', fromId: domainId };
    }
    return fallback;
  }

  function mdWorkflowFor(kind: unknown, id: unknown): MdWorkflow {
    return mdWorkflowById(mdWorkflowSourceFor(kind, id).workflowId) as MdWorkflow;
  }

  /** A record speaks the workflow of the PROJECT that holds it, and of its own
      domain when nothing does. Every record already carries a domain, so the
      second rung needs no new pointer anywhere. */
  function mdWorkflowForRecord(record: unknown): MdWorkflow {
    const row = record && typeof record === 'object' ? record as Record<string, unknown> : {};
    if (typeof row.pid === 'string' && row.pid && g.mdProjectById && g.mdProjectById(row.pid)) {
      return mdWorkflowFor('project', row.pid);
    }
    return mdWorkflowFor('domain', typeof row.domain === 'string' ? row.domain : '');
  }

  /** Assign, or clear. One document per gesture, and the document itself is
      forgotten once the last assignment in it is cleared — an account that
      tried one and changed its mind leaves nothing behind. */
  function mdWorkflowTrySetUse(kind: unknown, id: unknown, workflowId: unknown): MdWorkflowResult {
    const agent = agentRefusal();
    if (agent) return agent;
    if (typeof kind !== 'string' || !USE_PREFIX[kind] || typeof id !== 'string') {
      return refuse('invalid');
    }
    if (!nodeExists(kind, id)) return refuse('missing');
    const wipe = workflowId == null || workflowId === '';
    if (!wipe && !(typeof workflowId === 'string' && mdWorkflowById(workflowId))) {
      return refuse('invalid');
    }
    const use = readUse();
    if (wipe) delete use[USE_PREFIX[kind] + id];
    else use[USE_PREFIX[kind] + id] = workflowId as string;
    if (Object.keys(use).length) return g.mdWrite(K_USE, use) ? OK : writeRefusal();
    if (g.mdRead(K_USE, null) == null) return OK;
    return g.mdRemove(K_USE) ? OK : writeRefusal();
  }

  /* ═══ sync ════════════════════════════════════════════════════════════════
     One document for every workflow the account has named. The default is not
     among them — it is code, so an account that has never opened the Statuses
     page offers nothing here at all, and no merge can take it away. */

  function mdWorkflowSyncDocs(): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    // Absence of the KEY, not emptiness of the list: a person who deleted their
    // last workflow said something, and an empty document says it out loud.
    if (g.mdRead(K_INDEX, null) != null) out[DOC] = readIndex();
    if (g.mdRead(K_USE, null) != null) out[DOC_USE] = readUse();
    return out;
  }

  function mdWorkflowApplyDoc(key: unknown, value: unknown): boolean {
    if (key === DOC_USE) {
      if (value == null) return g.mdRemove(K_USE);
      if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
      return g.mdWrite(K_USE, value);        // readUse drops what it cannot read
    }
    if (key !== DOC) return false;
    if (value == null) return g.mdRemove(K_INDEX);
    if (!Array.isArray(value)) return false;
    const out: MdWorkflow[] = [];
    const seen = new Set<string>();
    for (const item of value.slice(0, MAX_WORKFLOWS)) {
      if (!isRow(item) || seen.has(item.id)) continue;
      seen.add(item.id);
      out.push(readRow(item));
    }
    return g.mdWrite(K_INDEX, out);
  }

  g.MD_SPINE = MD_SPINE;
  g.MD_WORKFLOW_DEFAULT_ID = DEFAULT_ID;
  Object.assign(g, { mdWorkflowIndex, mdWorkflowById, mdWorkflowStatusById,
    mdWorkflowCategoryOf, mdWorkflowCategoryLanding, mdWorkflowInboxOf,
    mdWorkflowResolveStatus,
    mdWorkflowFinishOf,
    mdWorkflowStatusLadder,
    mdWorkflowTryCreate, mdWorkflowTryRename, mdWorkflowTryDelete, mdWorkflowTryReset,
    mdWorkflowTryStatusCreate, mdWorkflowTryStatusRename, mdWorkflowTryStatusSetColor,
    mdWorkflowTryStatusMove, mdWorkflowTryStatusDelete,
    mdWorkflowUse, mdWorkflowFor, mdWorkflowSourceFor, mdWorkflowForRecord,
    mdWorkflowStatusRemovable,
    mdWorkflowTrySetUse, mdWorkflowSyncDocs, mdWorkflowApplyDoc });
})(typeof window !== 'undefined' ? window : globalThis);
